/**
 * Richer 2D schematic shapes for AMHS kit elements (top-down Konva).
 * Colors align with the 3D kit: aluminum stocker, navy equipment accents, OHT body.
 */
import React from 'react';
import { Group, Rect, Line, Circle, Text as KonvaText } from 'react-konva';
import { Oht2D } from './amhs/Oht2D';
import type { Oht2DFoupKind, Oht2DStatus } from './amhs/Oht2D';
import { OHT_2D_PALETTE } from './amhs/Oht2D';
import { Stocker2D } from './amhs/Stocker2D';
import type { Stocker2DSlot, Stocker2DStatus } from './amhs/Stocker2D';
import { STOCKER_2D_COLORS } from './amhs/Stocker2D';
import { Equipment2D } from './amhs/Equipment2D';
import type { Equipment2DKind } from './amhs/Equipment2D';
import type { LayoutElement } from '../../types';
import { EQUIPMENT_2D_COLORS } from './amhs/Equipment2D';

/** Shared 2D AMHS schematic palette (matches OHT / Stocker / Equipment design system). */
export const AMHS_2D = {
  stockerBody: STOCKER_2D_COLORS.body,
  stockerDark: STOCKER_2D_COLORS.tray,
  stockerBay: STOCKER_2D_COLORS.inset,
  stockerAccent: STOCKER_2D_COLORS.accentBlue,
  stockerBase: STOCKER_2D_COLORS.outline,
  equipBody: EQUIPMENT_2D_COLORS.body,
  equipAccent: EQUIPMENT_2D_COLORS.panel,
  equipNavy: EQUIPMENT_2D_COLORS.outline,
  equipNavyBright: EQUIPMENT_2D_COLORS.base,
  equipBase: EQUIPMENT_2D_COLORS.base,
  equipTray: EQUIPMENT_2D_COLORS.pedestal,
  ohtBody: OHT_2D_PALETTE.body,
  ohtAccent: OHT_2D_PALETTE.accent,
  ohtRail: OHT_2D_PALETTE.hanger,
  ohtFoup: OHT_2D_PALETTE.foup,
  ohtGripper: OHT_2D_PALETTE.hanger,
  stroke: STOCKER_2D_COLORS.outline,
} as const;

type ShapeBase = {
  w: number;
  h: number;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
};

/** Front-facing FAB stocker — same visual language as Oht2D. */
export const Stocker2DShape: React.FC<
  ShapeBase & {
    id?: string;
    rows?: number;
    columns?: number;
    slots?: Stocker2DSlot[];
    occupiedCount?: number;
    rawCount?: number;
    processedCount?: number;
    status?: Stocker2DStatus;
    selected?: boolean;
    zoomLevel?: number;
    showLabel?: boolean;
    showCapacity?: boolean;
    /** Hit target for canvas select/drag. Default on for layout stockers. */
    listening?: boolean;
  }
> = ({
  w,
  h,
  opacity = 1,
  id = 'STK-01',
  rows,
  columns,
  slots,
  occupiedCount,
  rawCount,
  processedCount,
  status = 'available',
  selected = false,
  zoomLevel = 1,
  showLabel = true,
  showCapacity = true,
  listening = true,
}) => (
  <Stocker2D
    centered={false}
    width={w}
    height={h}
    id={id}
    rows={rows}
    columns={columns}
    slots={slots}
    occupiedCount={occupiedCount}
    rawCount={rawCount}
    processedCount={processedCount}
    status={status}
    selected={selected}
    zoomLevel={zoomLevel}
    showLabel={showLabel}
    showCapacity={showCapacity}
    opacity={opacity}
    listening={listening}
  />
);

type EquipKind = Equipment2DKind;

/** Front-facing EFEM / process tool — matches the white tower + load-port reference. */
export const Equipment2DShape: React.FC<
  ShapeBase & {
    kind?: EquipKind;
    fill?: string;
    loadPortCount?: number;
    occupiedPorts?: number[];
    dockEdge?: 'top' | 'bottom' | 'left' | 'right';
    portAnchors?: Array<{ rx: number; ry: number }>;
    simulationElement?: LayoutElement;
    portIds?: string[];
    foupSlotView?: import('./amhs/Equipment/equipmentFoupSlots').EquipmentFoupSlotView;
    empty?: boolean;
    selected?: boolean;
    zoomLevel?: number;
    listening?: boolean;
  }
> = ({
  w,
  h,
  kind = 'equipment',
  opacity = 1,
  loadPortCount,
  occupiedPorts,
  dockEdge,
  portAnchors,
  simulationElement,
  portIds,
  foupSlotView,
  empty,
  selected = false,
  zoomLevel = 1,
  stroke = EQUIPMENT_2D_COLORS.outline,
  strokeWidth,
  listening = true,
}) => (
  <Equipment2D
    centered={false}
    width={w}
    height={h}
    kind={kind}
    loadPortCount={loadPortCount}
    occupiedPorts={occupiedPorts}
    dockEdge={dockEdge}
    portAnchors={portAnchors}
    simulationElement={simulationElement}
    portIds={portIds}
    foupSlotView={foupSlotView}
    empty={empty}
    selected={selected}
    zoomLevel={zoomLevel}
    opacity={opacity}
    stroke={stroke}
    strokeWidth={strokeWidth}
    listening={listening}
  />
);

/**
 * Compatibility wrapper for layout / simulation callers.
 * Prefer importing `Oht2D` from `./amhs/Oht2D` for new code.
 */
export const Oht2DShape: React.FC<
  ShapeBase & {
    loaded?: boolean;
    foupKind?: Oht2DFoupKind;
    name?: string;
    fill?: string;
    status?: Oht2DStatus;
    selected?: boolean;
    hovered?: boolean;
    showLabel?: boolean;
    zoomLevel?: number;
    /** Hit target for canvas select/drag. Default on for layout / sim OHTs. */
    listening?: boolean;
  }
> = ({
  w,
  h,
  /** Layout default: FOUP visible (Reference 2). Sim passes false when empty. */
  loaded = true,
  foupKind = 'raw',
  name,
  opacity = 1,
  status = 'idle',
  selected = false,
  hovered = false,
  showLabel = true,
  zoomLevel = 1,
  listening = true,
}) => (
  <Oht2D
    centered={false}
    width={w}
    height={h}
    showFoup={loaded}
    foupKind={foupKind}
    name={name}
    showLabel={showLabel}
    status={status}
    selected={selected}
    hovered={hovered}
    zoomLevel={zoomLevel}
    opacity={opacity}
    listening={listening}
  />
);

/** Route / gate START marker — teal pin with play chevron. */
export const StartNode2DShape: React.FC<{
  scale?: number;
  label?: string;
  showLabel?: boolean;
}> = ({ scale = 1, label = 'START', showLabel = true }) => {
  const s = scale;
  const r = 5 * s;
  return (
    <Group listening={false}>
      <Circle radius={r * 2.2} fill="#0d9488" opacity={0.18} />
      <Circle
        radius={r * 1.35}
        fill="#14b8a6"
        stroke="#0f766e"
        strokeWidth={1.2 * s}
      />
      <Circle radius={r * 0.55} fill="#ecfdf5" />
      {/* Play chevron */}
      <Line
        points={[
          -r * 0.15,
          -r * 0.55,
          r * 0.55,
          0,
          -r * 0.15,
          r * 0.55,
        ]}
        closed
        fill="#0f766e"
        strokeEnabled={false}
      />
      {showLabel && (
        <>
          <Rect
            x={-18 * s}
            y={-r * 2.8 - 10 * s}
            width={36 * s}
            height={11 * s}
            fill="#0f766e"
            cornerRadius={2 * s}
            opacity={0.92}
          />
          <KonvaText
            text={label}
            x={-18 * s}
            y={-r * 2.8 - 10 * s}
            width={36 * s}
            height={11 * s}
            align="center"
            verticalAlign="middle"
            fontSize={7 * s}
            fontStyle="bold"
            fill="#ecfdf5"
          />
        </>
      )}
    </Group>
  );
};

/** Route / gate END marker — rose pin with stop bar. */
export const EndNode2DShape: React.FC<{
  scale?: number;
  label?: string;
  showLabel?: boolean;
}> = ({ scale = 1, label = 'END', showLabel = true }) => {
  const s = scale;
  const r = 5 * s;
  return (
    <Group listening={false}>
      <Circle radius={r * 2.2} fill="#e11d48" opacity={0.18} />
      <Circle
        radius={r * 1.35}
        fill="#f43f5e"
        stroke="#be123c"
        strokeWidth={1.2 * s}
      />
      <Circle radius={r * 0.55} fill="#fff1f2" />
      {/* Stop bar */}
      <Rect
        x={-r * 0.45}
        y={-r * 0.35}
        width={r * 0.9}
        height={r * 0.7}
        fill="#be123c"
        cornerRadius={0.8 * s}
      />
      {showLabel && (
        <>
          <Rect
            x={-14 * s}
            y={-r * 2.8 - 10 * s}
            width={28 * s}
            height={11 * s}
            fill="#be123c"
            cornerRadius={2 * s}
            opacity={0.92}
          />
          <KonvaText
            text={label}
            x={-14 * s}
            y={-r * 2.8 - 10 * s}
            width={28 * s}
            height={11 * s}
            align="center"
            verticalAlign="middle"
            fontSize={7 * s}
            fontStyle="bold"
            fill="#fff1f2"
          />
        </>
      )}
    </Group>
  );
};

/** Entry/Exit gate plaque used on track nodes. */
export const GateNode2DShape: React.FC<{
  role: 'entry' | 'exit';
  width: number;
  height: number;
  label: string;
  selected?: boolean;
  zoomLevel?: number;
}> = ({ role, width, height, label: _label, selected = false, zoomLevel = 1 }) => {
  const isEntry = role === 'entry';
  const fill = isEntry ? '#0d9488' : '#e11d48';
  const stroke = selected ? '#1d4ed8' : isEntry ? '#0f766e' : '#be123c';
  const accent = isEntry ? '#5eead4' : '#fda4af';
  const sw = (selected ? 2.2 : 1.4) / zoomLevel;

  return (
    <Group>
      <Rect
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        fill={fill}
        opacity={0.92}
        stroke={stroke}
        strokeWidth={sw}
        cornerRadius={3 / zoomLevel}
        hitStrokeWidth={8 / zoomLevel}
      />
      {/* Accent strip */}
      <Rect
        x={-width / 2}
        y={-height / 2}
        width={width * 0.14}
        height={height}
        fill={accent}
        opacity={0.85}
      />
      {/* Icon hint — no text label (avoids truncated E/Exit glyphs at low zoom). */}
      {isEntry ? (
        <Line
          points={[
            -width * 0.08,
            -height * 0.28,
            width * 0.28,
            0,
            -width * 0.08,
            height * 0.28,
          ]}
          closed
          fill="#ecfdf5"
          opacity={0.95}
        />
      ) : (
        <Rect
          x={-width * 0.1}
          y={-height * 0.22}
          width={width * 0.28}
          height={height * 0.44}
          fill="#fff1f2"
          cornerRadius={1 / zoomLevel}
        />
      )}
    </Group>
  );
};
